import React from 'react';
import { Card, CardContent } from './ui/card';
import { CheckCircle2, Calendar, Users, Building2, GraduationCap, TrendingUp, Target, Rocket } from 'lucide-react';

export const TenYearPlan = () => {
  const phases = [
    {
      phase: 'Foundation',
      years: 'Years 1-2',
      yearRange: '2025-2027',
      color: 'tealAccent',
      icon: Rocket,
      tagline: 'Building the Blueprint',
      objectives: [
        'Complete charter petition and secure approval',
        'Hire education attorney and charter consultant',
        'Acquire 38-acre property in Winchester ($2M)',
        'Form inaugural Board of Directors',
        'Begin architectural planning'
      ],
      students: 'N/A (Pre-enrollment)',
      staff: '5-7 Board Members',
      facilities: '38-acre property acquisition ($2M)',
      budget: '$2M - $2.4M',
      keyMilestones: [
        'Charter approval (Nov-Dec 2027)',
        'Land purchase completed ($2M)',
        'CSP Grant application'
      ]
    },
    {
      phase: 'Planning & Design',
      years: 'Years 3-4',
      yearRange: '2027-2029',
      color: 'goldenOlive',
      icon: Building2,
      tagline: 'From Vision to Blueprints',
      objectives: [
        'Complete master plan and architectural designs',
        'Secure all entitlements and permits',
        'Finalize $25-35M construction financing',
        'Hire Executive Director/Principal',
        'Develop comprehensive curriculum frameworks'
      ],
      students: 'N/A (Pre-enrollment)',
      staff: 'Executive Director + 4 Leadership roles',
      facilities: '95,000 sq ft campus design (Academic, Arts, Library, Gym, Auditorium)',
      budget: '$2.4M - $3.1M',
      keyMilestones: [
        'Building permits obtained (Jan 2029)',
        'Construction contract awarded (May 2029)',
        'Curriculum frameworks developed'
      ]
    },
    {
      phase: 'Construction',
      years: 'Years 5-6',
      yearRange: '2029-2031',
      color: 'sageGreen',
      icon: Building2,
      tagline: 'Building Our Future',
      objectives: [
        'Complete Phase 1 construction (Academic, Admin, Cafeteria)',
        'Hire full administrative and inaugural teaching staff',
        'Achieve enrollment target: 200 sixth-graders',
        'Conduct comprehensive staff professional development',
        'Set up all operational systems'
      ],
      students: '200 (Grade 6 enrolled for Aug 2031)',
      staff: '12 Teachers + Admin + Support',
      facilities: 'Phase 1: 51,000 sq ft operational',
      budget: '$23.1M - $28.6M',
      keyMilestones: [
        'Phase 1 construction completed',
        'Certificate of Occupancy obtained',
        '200 students enrolled'
      ]
    },
    {
      phase: 'Grand Opening',
      years: 'Year 7',
      yearRange: '2031-2032',
      color: 'coralRed',
      icon: GraduationCap,
      tagline: 'Opening Our Doors',
      objectives: [
        'Successfully open to 225 students (100/75/50 across grades 6-8)',
        'Establish educator-led Academic Senate model',
        'Implement all curriculum programs',
        'Begin Phase 2 construction (Gym, Arts, Community Center)',
        'Launch recruitment for Year 2 expansion'
      ],
      students: '225 (100 Grade 6, 75 Grade 7, 50 Grade 8)',
      staff: '12 Core Teachers, 1 SPED, 2 Counselors, Principal, VP, 12 Aides',
      facilities: 'Phase 1 operational; Phase 2 begins Feb',
      budget: '$3.4M revenue',
      keyMilestones: [
        'First day: August 11, 2031',
        'Academic Senate established',
        'First parent-teacher conferences'
      ]
    },
    {
      phase: 'Expansion',
      years: 'Year 8',
      yearRange: '2032-2033',
      color: 'tealAccent',
      icon: TrendingUp,
      tagline: 'Growing with Purpose',
      objectives: [
        'Expand to 400 students (Grades 6-7)',
        'Complete Phase 2 construction',
        'Add 12 additional teachers and support staff',
        'Launch full arts program and athletics',
        'Host inaugural Fall Festival'
      ],
      students: '400 (Grades 6-7, 200 per grade)',
      staff: '24 Teachers, 6 SPED, 3 Counselors, 20 Aides',
      facilities: 'Phase 2 complete: Gym, Arts Building, Community Center',
      budget: '$6.4M revenue',
      keyMilestones: [
        'Phase 2 facilities operational',
        'First Science & History Fairs',
        'Community education expanded'
      ]
    },
    {
      phase: 'Full Capacity',
      years: 'Years 9-10',
      yearRange: '2033-2035',
      color: 'goldenOlive',
      icon: Target,
      tagline: 'Achieving Excellence',
      objectives: [
        'Reach full enrollment: 600 students (Grades 6-8)',
        'Complete all facilities (Library, Auditorium, Track)',
        'Hold first 8th-grade graduation',
        'Build 10% operating reserve fund',
        'Plan for charter renewal'
      ],
      students: '600 (Grades 6-8, 200 per grade)',
      staff: '36 Teachers, 6 SPED, 3 Counselors, 31 Aides + Full Support',
      facilities: 'Phase 3 complete: All 95,000 sq ft operational',
      budget: '$9.6M revenue, $360K annual surplus',
      keyMilestones: [
        'First 8th-grade graduation (June 2034)',
        'Financial sustainability achieved',
        'Charter renewal prepared'
      ]
    }
  ];

  return (
    <section id="ten-year-plan" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="container mx-auto max-w-7xl">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-block bg-navyBlue/10 px-6 py-2 rounded-full mb-4">
            <span className="text-navyBlue font-semibold">Our Roadmap</span>
          </div>
          <h2 className="text-4xl sm:text-5xl font-bold text-navyBlue mb-6">10-Year Strategic Implementation Plan</h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            From charter approval to full operational maturity (2025-2035): A transparent roadmap for families, educators, donors, and board members.
          </p>
        </div>

        {/* Investment Overview */}
        <div className="bg-gradient-to-br from-navyBlue to-navyBlue/90 rounded-2xl p-8 sm:p-12 mb-16 text-white">
          <h3 className="text-3xl font-bold mb-8 text-center">Total Investment Summary</h3>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-tealAccent mb-2">$27M-37M</div>
              <p className="text-white/90">Total Investment</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-goldenOlive mb-2">$25M-35M</div>
              <p className="text-white/90">Construction Costs</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-sageGreen mb-2">$2M</div>
              <p className="text-white/90">Land Acquisition</p>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-coralRed mb-2">600</div>
              <p className="text-white/90">Students at Full Capacity</p>
            </div>
          </div>
        </div>

        {/* Timeline Phases */}
        <div className="space-y-8">
          {phases.map((phase, index) => {
            const Icon = phase.icon;
            return (
              <Card key={index} className={`border-2 border-${phase.color}/20 hover:border-${phase.color}/40 transition-all duration-300 hover:shadow-2xl overflow-hidden`}>
                <CardContent className="p-0">
                  <div className="grid lg:grid-cols-3 gap-0">
                    {/* Phase Header */}
                    <div className={`lg:col-span-1 bg-${phase.color}/10 p-8 flex flex-col justify-center border-r-2 border-${phase.color}/20`}>
                      <div className={`bg-${phase.color}/20 w-16 h-16 rounded-2xl flex items-center justify-center mb-4`}>
                        <Icon className={`text-${phase.color}`} size={32} />
                      </div>
                      <h3 className="text-3xl font-bold text-navyBlue mb-2">{phase.phase}</h3>
                      <p className={`text-xl text-${phase.color} font-semibold mb-4`}>{phase.tagline}</p>
                      <div className="flex items-center space-x-2 text-gray-600 mb-2">
                        <Calendar size={18} />
                        <span className="font-medium">{phase.years}</span>
                      </div>
                      <p className="text-sm text-gray-500">{phase.yearRange}</p>
                    </div>

                    {/* Phase Details */}
                    <div className="lg:col-span-2 p-8 space-y-6">
                      {/* Objectives */}
                      <div>
                        <h4 className="text-lg font-bold text-navyBlue mb-3 flex items-center space-x-2">
                          <CheckCircle2 className={`text-${phase.color}`} size={20} />
                          <span>Primary Objectives</span>
                        </h4>
                        <ul className="space-y-2">
                          {phase.objectives.map((obj, idx) => (
                            <li key={idx} className="flex items-start space-x-2">
                              <span className={`text-${phase.color} mt-1 flex-shrink-0`}>•</span>
                              <span className="text-gray-700">{obj}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Key Metrics Grid */}
                      <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <Users className={`text-${phase.color}`} size={18} />
                            <span className="font-semibold text-navyBlue text-sm">Students & Staff</span>
                          </div>
                          <p className="text-gray-700 text-sm mb-1"><strong>Students:</strong> {phase.students}</p>
                          <p className="text-gray-700 text-sm"><strong>Staff:</strong> {phase.staff}</p>
                        </div>

                        <div className="bg-gray-50 rounded-lg p-4">
                          <div className="flex items-center space-x-2 mb-2">
                            <Building2 className={`text-${phase.color}`} size={18} />
                            <span className="font-semibold text-navyBlue text-sm">Facilities & Budget</span>
                          </div>
                          <p className="text-gray-700 text-sm mb-1"><strong>Facilities:</strong> {phase.facilities}</p>
                          <p className="text-gray-700 text-sm"><strong>Budget:</strong> {phase.budget}</p>
                        </div>
                      </div>

                      {/* Key Milestones */}
                      <div className={`bg-${phase.color}/5 rounded-lg p-4 border-l-4 border-${phase.color}`}>
                        <h5 className="font-semibold text-navyBlue mb-2 text-sm">Key Milestones:</h5>
                        <div className="flex flex-wrap gap-2">
                          {phase.keyMilestones.map((milestone, idx) => (
                            <span key={idx} className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-white border border-gray-300 text-gray-700">
                              {milestone}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Success Factors */}
        <div className="mt-16 bg-gradient-to-br from-tealAccent/10 to-sageGreen/10 rounded-2xl p-8 sm:p-12 border-2 border-tealAccent/20">
          <h3 className="text-3xl font-bold text-navyBlue mb-8 text-center">Key Success Factors</h3>
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="text-4xl mb-3">💡</div>
              <h4 className="text-lg font-bold text-navyBlue mb-2">Strong Vision</h4>
              <p className="text-gray-600">Founder-driven innovation with clear educational philosophy and community commitment</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="text-4xl mb-3">🏛️</div>
              <h4 className="text-lg font-bold text-navyBlue mb-2">Innovative Governance</h4>
              <p className="text-gray-600">Academic Senate model empowering teachers to lead curriculum and policy decisions</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="text-4xl mb-3">📈</div>
              <h4 className="text-lg font-bold text-navyBlue mb-2">Phased Growth</h4>
              <p className="text-gray-600">Careful grade-by-grade expansion ensuring quality and financial sustainability</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="text-4xl mb-3">🤝</div>
              <h4 className="text-lg font-bold text-navyBlue mb-2">Community Integration</h4>
              <p className="text-gray-600">Deep partnerships with families, businesses, and local organizations</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="text-4xl mb-3">💰</div>
              <h4 className="text-lg font-bold text-navyBlue mb-2">Financial Prudence</h4>
              <p className="text-gray-600">Strategic budgeting with 10% operating reserve goal by Year 10</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-md">
              <div className="text-4xl mb-3">🎓</div>
              <h4 className="text-lg font-bold text-navyBlue mb-2">Comprehensive Programs</h4>
              <p className="text-gray-600">Research-based curriculum, arts, athletics, and community education</p>
            </div>
          </div>
        </div>

        {/* Looking Forward */}
        <div className="mt-16 text-center">
          <div className="bg-navyBlue rounded-2xl p-8 sm:p-12 text-white">
            <h3 className="text-3xl font-bold mb-4">The Promise of Doctrina</h3>
            <p className="text-lg text-white/90 max-w-4xl mx-auto leading-relaxed mb-6">
              By 2035, Doctrina Charter School will have transformed the educational landscape of the Inland Empire, serving 600 students with educators who lead with passion and purpose, families who are active partners, and students equipped with knowledge, confidence, and character.
            </p>
            <p className="text-xl font-semibold text-tealAccent">
              This 10-year journey is ambitious but achievable—a movement to heal and build something beautiful in public education.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TenYearPlan;